/**
 * Shared writer-identity & time utilities.
 *
 * Extracted here to eliminate duplication across FileBrowser, EditorView,
 * HistoryPanel, blameGutter, SyncDetailPopover, TopBar, and WorkspacePicker.
 */

// ── Writer Color ───────────────────────────────────────────────

/** Deterministic HSL color from a key-ID string. */
export function writerColor(kid: string | null | undefined): string {
    if (!kid) return 'hsl(0, 0%, 50%)'
    let hash = 0
    for (let i = 0; i < kid.length; i++) hash = ((hash << 5) - hash + kid.charCodeAt(i)) | 0
    return `hsl(${Math.abs(hash) % 360}, 55%, 55%)`
}

// ── Relative Time ──────────────────────────────────────────────

/**
 * Format an epoch-ms timestamp (or ISO string) as a compact relative string.
 *
 * @param ts  epoch ms (number) or ISO string — falsy → returns `fallback`
 * @param opts.fallback  returned when `ts` is falsy (default: `''`)
 * @param opts.suffix    appended after the value (default: `''`, set to `' ago'` for English)
 * @param opts.maxDepth  how many time-units to support:
 *    - `'seconds'` — `just now`, `34s`, `5m`, `2h` (for sync/polling UIs)
 *    - `'days'`    — `just now`, `5m`, `2h`, `3d` (for history/commit UIs)
 *    - `'years'`   — `just now`, `5m`, `2h`, `3d`, `2w`, `3mo`, `1y` (for blame/file metadata)
 *    Default: `'years'`
 */
export function timeAgo(
    ts: number | string | null | undefined,
    opts: { fallback?: string; suffix?: string; maxDepth?: 'seconds' | 'days' | 'years' } = {},
): string {
    const { fallback = '', suffix = '', maxDepth = 'years' } = opts
    if (!ts) return fallback

    const epoch = typeof ts === 'string' ? new Date(ts).getTime() : ts
    const diff = Math.max(0, Date.now() - epoch)

    const secs = Math.floor(diff / 1000)
    const mins = Math.floor(secs / 60)
    const hrs = Math.floor(mins / 60)
    const days = Math.floor(hrs / 24)

    if (secs < 5) return 'just now'

    if (maxDepth === 'seconds') {
        if (secs < 60) return `${secs}s${suffix}`
        if (mins < 60) return `${mins}m${suffix}`
        return `${hrs}h${suffix}`
    }

    if (mins < 1) return 'just now'
    if (mins < 60) return `${mins}m${suffix}`
    if (hrs < 24) return `${hrs}h${suffix}`

    if (maxDepth === 'days') {
        return `${days}d${suffix}`
    }

    // maxDepth === 'years'
    if (days < 7) return `${days}d${suffix}`
    if (days < 30) return `${Math.floor(days / 7)}w${suffix}`
    if (days < 365) return `${Math.floor(days / 30)}mo${suffix}`
    return `${Math.floor(days / 365)}y${suffix}`
}

// ── Exact Time (for tooltips) ──────────────────────────────────

/**
 * Format an epoch-ms timestamp as a precise local date + time string.
 * Designed for hover tooltips where users want the exact moment.
 *
 * Example: "2026-02-17 22:48:03"
 */
export function formatExactTime(ts: number | string | null | undefined): string {
    if (!ts) return ''
    const epoch = typeof ts === 'string' ? new Date(ts).getTime() : ts
    if (!epoch || epoch <= 0) return ''
    const d = new Date(epoch)
    return d.toLocaleDateString('sv') + ' ' + d.toLocaleTimeString('sv')
}
